import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Button } from "components/common/Button";
import { TooltipButton } from "components/common/TooltipButton";
import { useEscapeStack } from "components/hooks/useEscapeStack";
import { durationMs, prefersReducedMotion } from "@/lib/motion";
import { vars, type Box } from "./story";
import "./spotlightTour.css";

interface Point {
  x: number;
  y: number;
}

export interface TourStep {
  title: string;
  body: string;
  /** What the step lights: their union, or none for a card in the middle. */
  target: () => Element[];
  /** Where the card sits beside the light. */
  side: "right" | "below";
  /** Its frame's `data-came` says how it follows the last picture shown. */
  illustration?: React.ReactNode;
  /** Run as the step opens, e.g. to unfold what the light points at. */
  prepare?: () => void;
  /** The light's room around its target, PAD unless set: 0 hugs it, and -1
   * lays the light's edge on a panel's own border. */
  pad?: number;
}

const PAD = 6; // the light's breathing room around its target
const INSET = 1; // the outline's width: it stays on screen around a target at the edge
const GAP = 14; // between the light and the card
const EDGE = 12; // the card never comes closer than this to the window's edge
const RIPPLE = 6; // how far past the light its landing ring travels
// Its picture is then 320 × 180 px (padding and hairline border aside), one
// unit to a pixel, so a drawn picture's text and icons are the app's own sizes.
const CARD_W = 354;
// Critically damped springs: no overshoot, and they follow a goal that moves.
const OMEGA = 13; // rad/s: there in about 0.4 s
// The card's spring is a little softer, so the light leads and the card follows.
const CARD_OMEGA = 11;
// Landed this close and slow: the spring's last pixels are a tail no one sees.
const LANDED_PX = 6;
const LANDED_SPEED = 60; // px/s
const EXIT_MS = 260;

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

// The light's 1px edge is drawn just outside it, so a pad of -1 puts the edge
// on the target's own outermost pixel.
function measure(elements: Element[], pad: number): Box | null {
  const rects = elements
    .map((e) => e.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0);
  if (!rects.length) return null;
  const x = Math.max(INSET, Math.min(...rects.map((r) => r.left)) - pad);
  const y = Math.max(INSET, Math.min(...rects.map((r) => r.top)) - pad);
  const right = Math.min(
    window.innerWidth - INSET,
    Math.max(...rects.map((r) => r.right)) + pad,
  );
  const bottom = Math.min(
    window.innerHeight - INSET,
    Math.max(...rects.map((r) => r.bottom)) + pad,
  );
  return { x, y, w: right - x, h: bottom - y };
}

const appRadius = () =>
  parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue("--radius-app"),
  ) || 0;

// A rectangle with the app's corner cut out of the whole window: the dim draws
// everywhere but the light, and catches clicks there too (`evenodd`).
function scrimPath(r: Box, radius: number): string {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const k = Math.min(radius, r.w / 2, r.h / 2);
  const { x, y, w, h } = r;
  return (
    `M0 0H${W}V${H}H0Z ` +
    `M${x + k} ${y}H${x + w - k}Q${x + w} ${y} ${x + w} ${y + k}` +
    `V${y + h - k}Q${x + w} ${y + h} ${x + w - k} ${y + h}` +
    `H${x + k}Q${x} ${y + h} ${x} ${y + h - k}V${y + k}Q${x} ${y} ${x + k} ${y}Z`
  );
}

type Side = "right" | "left" | "below" | "above";
type Arrow = "left" | "right" | "top" | "bottom";
// The card's arrow sits on the edge that faces the light.
const ARROW: Record<Side, Arrow> = {
  right: "left",
  left: "right",
  below: "top",
  above: "bottom",
};
const TRY: Record<TourStep["side"], Side[]> = {
  right: ["right", "below", "above", "left"],
  below: ["below", "above", "left", "right"],
};

// The first side, in the step's order, where the card fits the window without
// covering what it explains; else the preferred one, held inside the window.
function place(
  light: Box,
  side: TourStep["side"],
  card: { w: number; h: number },
): Point & { arrow: Arrow } {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const beside = clamp(light.y, EDGE, H - card.h - EDGE);
  const under = clamp(light.x + light.w - card.w, EDGE, W - card.w - EDGE);
  const spots: Record<Side, Point & { fits: boolean }> = {
    right: {
      x: light.x + light.w + GAP,
      y: beside,
      fits: light.x + light.w + GAP + card.w <= W - EDGE,
    },
    left: {
      x: light.x - GAP - card.w,
      y: beside,
      fits: light.x - GAP - card.w >= EDGE,
    },
    below: {
      x: under,
      y: light.y + light.h + GAP,
      fits: light.y + light.h + GAP + card.h <= H - EDGE,
    },
    above: {
      x: under,
      y: light.y - GAP - card.h,
      fits: light.y - GAP - card.h >= EDGE,
    },
  };
  const order = TRY[side];
  const chosen = order.find((s) => spots[s].fits) ?? order[0];
  const spot = spots[chosen];
  return {
    x: clamp(spot.x, EDGE, W - card.w - EDGE),
    y: clamp(spot.y, EDGE, H - card.h - EDGE),
    arrow: ARROW[chosen],
  };
}

const middle = (): Box => ({
  x: window.innerWidth / 2,
  y: window.innerHeight / 2,
  w: 0,
  h: 0,
});

const centreOf = (r: Box): Box => ({
  x: r.x + r.w / 2,
  y: r.y + r.h / 2,
  w: 0,
  h: 0,
});

const centred = (card: { w: number; h: number }): Point => ({
  x: (window.innerWidth - card.w) / 2,
  y: (window.innerHeight - card.h) / 2,
});

/** One step of a critically damped spring; returns how far it still has to go. */
function spring<K extends string>(
  current: Record<K, number>,
  velocity: Record<K, number>,
  goal: Record<K, number>,
  dt: number,
  omega = OMEGA,
): { distance: number; speed: number } {
  let distance = 0;
  let speed = 0;
  for (const k of Object.keys(goal) as K[]) {
    if (prefersReducedMotion()) {
      current[k] = goal[k];
      velocity[k] = 0;
    } else {
      velocity[k] +=
        (omega * omega * (goal[k] - current[k]) - 2 * omega * velocity[k]) * dt;
      current[k] += velocity[k] * dt;
    }
    distance += Math.abs(goal[k] - current[k]);
    speed += Math.abs(velocity[k]);
  }
  return { distance, speed };
}

/**
 * Words that change with the step: the old ones slip out where they stood
 * before the new ones come in, so there is neither a cut nor an overlap.
 */
function Handover({
  turn,
  children,
}: {
  turn: number;
  children: React.ReactNode;
}): React.JSX.Element {
  const [last, setLast] = useState({ turn, children });
  const [leaving, setLeaving] = useState<React.ReactNode>(null);
  if (turn !== last.turn) {
    setLeaving(last.children);
    setLast({ turn, children });
  }
  useEffect(() => {
    if (leaving === null) return;
    const timer = setTimeout(
      () => setLeaving(null),
      durationMs("--duration-micro", 100),
    );
    return () => clearTimeout(timer);
  }, [leaving]);
  // Sized to the incoming words, so the card eases to a new length, not snaps.
  const incoming = useRef<HTMLSpanElement>(null);
  const [height, setHeight] = useState<number>();
  useLayoutEffect(() => {
    const el = incoming.current;
    if (!el) return;
    const read = () => setHeight(el.offsetHeight);
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [turn]);
  return (
    <span className="tour__handover" style={{ height }}>
      {leaving !== null && (
        <span className="tour__text tour__text--leaving" aria-hidden="true">
          {leaving}
        </span>
      )}
      <span key={turn} ref={incoming} className="tour__text">
        {children}
      </span>
    </span>
  );
}

/**
 * A guided tour over the real page: one card that travels with a light from
 * part to part, its words and picture moving on as it goes.
 */
export function SpotlightTour({
  label,
  steps,
  welcome,
  skipWelcome = false,
  width = CARD_W,
  onClose,
}: {
  label: string;
  steps: TourStep[];
  /** A card that offers the tour before it starts; its light opens from the middle. */
  welcome?: { title: string; body: string; illustration?: React.ReactNode };
  skipWelcome?: boolean;
  /** Wider for a recorded picture, so it plays at its own size. */
  width?: number;
  onClose: () => void;
}): React.JSX.Element {
  const offering = !!welcome && !skipWelcome;
  // -1 is the welcome, which only ever comes first.
  const [index, setIndex] = useState(offering ? -1 : 0);
  const [closing, setClosing] = useState(false);
  const [frame, setFrame] = useState<{ light: Box; card: Point | null }>(
    // No light to begin with, welcome or not: the first one opens from what it lights.
    () => ({ light: middle(), card: null }),
  );
  // The card waits for the light's first landing, then stays up.
  const [appeared, setAppeared] = useState(false);
  const [landed, setLanded] = useState(false);
  // Reset in the render that changes the step: an effect ran a frame late.
  const [visit, setVisit] = useState(index);
  if (visit !== index) {
    setVisit(index);
    setLanded(false);
  }
  const live = useRef({
    light: frame.light,
    lightV: { x: 0, y: 0, w: 0, h: 0 },
    card: null as Point | null,
    cardV: { x: 0, y: 0 },
    cardSize: { w: width, h: 200 },
    arrow: null as Arrow | null,
    closeTo: null as Box | null,
    appeared: false,
    landed: false,
  });
  const cardRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const step = index < 0 ? null : steps[index];
  const last = index === steps.length - 1;
  const page = step ?? welcome;

  const close = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(onClose, prefersReducedMotion() ? 0 : EXIT_MS);
  };
  const go = (to: number) =>
    setIndex(Math.max(Math.min(index, 0), Math.min(steps.length - 1, to)));
  const next = () => (last ? close() : go(index + 1));

  useEscapeStack(true, () => close());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight")
        setIndex((i) => Math.min(steps.length - 1, i + 1));
      if (e.key === "ArrowLeft")
        setIndex((i) => Math.max(Math.min(i, 0), i - 1));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [steps.length]);

  // The page behind can be neither focused nor clicked while the tour holds it.
  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;
    root.inert = true;
    nextRef.current?.focus();
    return () => {
      root.inert = false;
    };
  }, []);

  useEffect(() => step?.prepare?.(), [step]);

  // Live, so the card is placed for the size it is growing to, not the last one.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const read = () => {
      live.current.cardSize = { w: card.offsetWidth, h: card.offsetHeight };
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  // The frame keeps the last picture shown, so a step without one folds it
  // away; a new picture continues only from the one just before it.
  const picture = useRef<{
    key: number;
    node: React.ReactNode;
    came?: "forward" | "back" | "still" | "jump";
  } | null>(null);
  // On the settled pass only: the one that resets `visit` still holds the old step.
  if (visit === index && page?.illustration && picture.current?.key !== index) {
    const last = picture.current?.key;
    picture.current = {
      key: index,
      node: page.illustration,
      came:
        last === undefined
          ? undefined
          : last === index - 1
            ? "forward"
            : last === index + 1
              ? "back"
              : last > index
                ? "still"
                : "jump",
    };
  }

  useEffect(() => {
    const s = live.current;
    s.landed = false;
    s.closeTo = null;
    let raf = 0;
    let then = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(1 / 30, (now - then) / 1000);
      then = now;
      const lightable = step && measure(step.target(), step.pad ?? PAD);
      // With nothing to light, the light closes into its own centre rather
      // than travelling to the middle of the window.
      if (!lightable) s.closeTo ??= centreOf(s.light);
      const target = lightable ?? s.closeTo ?? middle();
      // Closing, the light holds still while the dim fades around it.
      const goal = closing ? s.light : target;
      // Placed against the target, not the light on its way, so it never flips sides.
      const spot =
        lightable && step ? place(lightable, step.side, s.cardSize) : null;
      const cardGoal = spot ? { x: spot.x, y: spot.y } : centred(s.cardSize);
      s.arrow = spot?.arrow ?? null;
      // A light where there was none opens from the middle of what it lights,
      // rather than coming in from wherever the card last was.
      if (lightable && !closing && s.light.w < 1 && s.light.h < 1) {
        s.light = centreOf(lightable);
        s.lightV = { x: 0, y: 0, w: 0, h: 0 };
      }
      const light = { ...s.light };
      const lit = spring(light, s.lightV, goal, dt);
      // Unseen until it first appears, so it starts where it belongs.
      const card = { ...(s.card ?? cardGoal) };
      const moved =
        s.appeared && !closing
          ? spring(card, s.cardV, cardGoal, dt, CARD_OMEGA)
          : { distance: 0, speed: 0 };
      if (!s.appeared) Object.assign(card, cardGoal);
      if (
        lit.distance + lit.speed + moved.distance + moved.speed > 0.05 ||
        !s.card
      ) {
        s.light = light;
        s.card = card;
        setFrame({ light, card });
      }
      if (
        !s.landed &&
        !closing &&
        lit.distance < LANDED_PX &&
        lit.speed < LANDED_SPEED &&
        moved.distance < LANDED_PX &&
        moved.speed < LANDED_SPEED
      ) {
        s.landed = true;
        s.appeared = true;
        setLanded(true);
        setAppeared(true);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [step, closing]);

  const shown = appeared && !closing;
  const { light } = frame;
  const card = frame.card ?? centred(live.current.cardSize);
  const arrow = live.current.arrow;
  const sideways = arrow === "left" || arrow === "right";
  const size = live.current.cardSize;
  // The arrow aims at the middle of the light, kept on the card's edge.
  const aim = sideways
    ? clamp(light.y + light.h / 2 - card.y, 14, size.h - 14)
    : clamp(light.x + light.w / 2 - card.x, 14, size.w - 14);
  // Kept while closing, so it fades with the dim rather than vanishing first.
  const lit = light.w > 1 && light.h > 1;
  const [radius] = useState(appRadius);
  // The hole snaps to device pixels and its 1px edge lies just outside it, so
  // the line sits on whole pixels instead of smearing across two.
  const dpr = window.devicePixelRatio || 1;
  const snap = (v: number) => Math.round(v * dpr) / dpr;
  const hole = {
    x: snap(light.x),
    y: snap(light.y),
    w: snap(light.x + light.w) - snap(light.x),
    h: snap(light.y + light.h) - snap(light.y),
  };
  const edge = {
    x: hole.x - 0.5,
    y: hole.y - 0.5,
    width: hole.w + 1,
    height: hole.h + 1,
    rx: radius + 0.5,
  };
  const W = window.innerWidth;
  const H = window.innerHeight;

  return createPortal(
    <div className={`tour${closing ? " tour--closing" : ""}`}>
      <svg
        className="tour__scrim"
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        aria-hidden="true"
      >
        <path
          d={scrimPath(hole, radius)}
          fillRule="evenodd"
          className={`transition-colors ${step ? "fill-black/50" : "fill-black/30"}`}
          onClick={(e) => e.stopPropagation()}
        />
        {lit && (
          <g className={landed && visit === index ? "tour__flare" : undefined}>
            <rect className="tour__light" {...edge} />
            {landed && visit === index && (
              <rect
                key={index}
                className="tour__ripple"
                {...edge}
                style={vars({
                  "--tour-sx": String((edge.width + 2 * RIPPLE) / edge.width),
                  "--tour-sy": String((edge.height + 2 * RIPPLE) / edge.height),
                })}
              />
            )}
          </g>
        )}
      </svg>
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={`tour__card tour__card--from-${arrow ?? "middle"}`}
        data-shown={shown}
        style={{ left: card.x, top: card.y, width }}
      >
        {arrow && (
          <span
            className={`tour__arrow tour__arrow--${arrow}`}
            data-shown={landed && visit === index}
            style={sideways ? { top: aim } : { left: aim }}
            aria-hidden="true"
          />
        )}
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-fg-title">
            <Handover turn={index}>{page?.title}</Handover>
          </h2>
          {/* Hidden, not gone, on the last step, so the header keeps its height. */}
          {step && (
            <Button
              size="xs"
              variant="quiet-link"
              className={`tour__soft${last ? " invisible" : ""}`}
              onClick={() => close()}
            >
              Skip
            </Button>
          )}
        </div>
        <div className="tour__frame" data-open={!!page?.illustration}>
          <div>
            {picture.current && (
              <div
                key={picture.current.key}
                className="tour__illustration"
                data-came={picture.current.came}
              >
                {picture.current.node}
              </div>
            )}
          </div>
        </div>
        <p className="tour__body text-fg-muted">
          <Handover turn={index}>
            {page?.body.split("\n").map((line) => (
              <span key={line} className="tour__line">
                {line}
              </span>
            ))}
          </Handover>
        </p>
        {/* Equal side columns keep the dots centred whichever buttons flank them. */}
        <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="flex justify-end">
            {!step ? (
              <Button size="sm" variant="quiet-link" onClick={() => close()}>
                Not now
              </Button>
            ) : (
              index > 0 && (
                <TooltipButton
                  title="Back"
                  aria-label="Back"
                  variant="default"
                  className="button-active-state"
                  size="sm"
                  onClick={() => go(index - 1)}
                >
                  <ChevronLeftIcon />
                </TooltipButton>
              )
            )}
          </div>
          {step ? (
            <span className="tour__dots" role="group" aria-label="Steps">
              {steps.map((s, i) => (
                // A pager dot is smaller than the shared Button's smallest control.
                // eslint-disable-next-line no-restricted-syntax
                <button
                  key={s.title}
                  type="button"
                  className="tour__dot"
                  aria-label={`Step ${i + 1}: ${s.title}`}
                  aria-current={i === index ? "step" : undefined}
                  onClick={() => go(i)}
                >
                  <span />
                </button>
              ))}
            </span>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-1">
            {!step || last ? (
              <Button
                size="sm"
                ref={nextRef}
                variant="default"
                className={`button-active-state border-transparent!${step ? " tour__soft" : ""}`}
                onClick={next}
              >
                {step ? "Got it" : "Start tour"}
              </Button>
            ) : (
              <TooltipButton
                ref={nextRef}
                title="Next"
                aria-label="Next"
                variant="default"
                className="button-active-state"
                size="sm"
                onClick={next}
              >
                <ChevronRightIcon />
              </TooltipButton>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
